/**
 * VaccinationRecordDialog (Ted #32a, E1.6b/E1.6c, #1156): one vaccination
 * record, opened from the Pet Profile to correct, verify, delete, or attach
 * the certificate. It also adds a record the desk types in.
 *
 * Ted, 2026-10-06: records marked expired, "no clear way to correct, verify,
 * or replace", and nowhere to upload the updated certificate. His AC: "I
 * correct the rabies expiry, mark it verified, delete the duplicate, and
 * attach the certificate, all from his profile."
 *
 * Counter Test: a week-one receptionist holding an owner's emailed
 * certificate opens the dog, taps the vaccine, and sees what the record says
 * and who stands behind it. The ONE primary action is the next thing the
 * record needs: "Mark verified" while it is unsigned, otherwise none. Every
 * other action is a ghost button, and Delete is a two-step confirm that names
 * exactly what goes.
 *
 * Every write re-reads the profile (`onChanged`) and the dialog renders the
 * record the server returned, so what shows is what saved (#614). The
 * dialog stays open on the same record afterwards, because the AC's four
 * steps happen in one sitting.
 *
 * States: default · busy (buttons disabled, "Saving…") · error (the server's
 * sentence, inputs kept) · success (a status line naming what changed) ·
 * gone (the record was deleted, so the dialog closes).
 *
 * ── Upload first (#1213) ──────────────────────────────────────────────
 *
 * Ted, 10-09: "Add vaccination starts with the upload. Upload the
 * certificate, type and dates fill in, I compare against the document
 * beside it, then Verify." So an add opens on ONE action, "Upload the
 * certificate". The file is read (POST …/vaccinations/parse, which writes
 * nothing) while it already shows beside the form — side by side on a
 * desk screen, stacked on a phone. The form fills from what the certificate
 * prints, each pre-filled field says so until it is changed, and a printed
 * expiry the certificate lacks is left empty to type.
 *
 * The read never decides anything. "Verify" is the primary action and the
 * only way the record is signed off; "Save, not verified" saves it exactly
 * as an unticked add does today. Either way the record is created
 * unverified, the same file is attached through the attach route (the one
 * storage path), and only then does Verify call /verify — so a record
 * verified here always has its certificate on file.
 *
 * A read that fails, has no reader configured, or finds no vaccine (Ted's
 * 10-08 bank record: "We couldn't read a vaccine from this document") leaves
 * the document beside an EMPTY form: the desk types it, never a dead end,
 * never a guess shown as fact. "No certificate? Type it in" keeps the
 * type-first form for a record with no paper at hand.
 */
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import {
  VACCINATION_SOURCE_LABEL,
  VACCINATION_STANDING_LABEL,
  parseVaccinationSource,
  vaccinationStanding,
} from '@ferro/core/modules/dog-profile/vaccination-source.js';
import {
  addDogVaccination,
  deleteDogVaccination,
  humanizeApiError,
  readDogVaccinationCertificate,
  readSession,
  updateDogVaccination,
  uploadDogVaccinationDocument,
  verifyDogVaccination,
  type DogProfileVaccination,
  type VaccinationCertificateRead,
} from '../../lib/api.js';
import { formatDate, vaccineLabel, vaxRowDisplay, VAX_ROW_LABEL } from './format.js';

export type VaccinationDialogMode =
  | { kind: 'add' }
  | { kind: 'manage'; record: DogProfileVaccination };

interface VaccinationRecordDialogProps {
  apiUrl: string;
  dogId: string;
  dogName: string;
  mode: VaccinationDialogMode;
  /** Vaccine names to suggest: the dog's own plus the usual three. */
  knownNames: ReadonlyArray<string>;
  /** Re-read the profile. Resolves once the new record is on the page. */
  onChanged: (focusVaccinationId?: string) => Promise<void> | void;
  onViewCertificate: (record: DogProfileVaccination) => void;
  /** #752 — this row's intake original, when it was approved from one. */
  onViewOriginal?: () => void;
  /**
   * #1213 — reopen this dialog in add mode for the next vaccine on the same
   * certificate. Without it the dialog does not offer one.
   */
  onAddAnother?: () => void;
  onClose: () => void;
}

/**
 * `upload` — an add starts here, on the certificate. `review` — the
 * certificate beside the form. `edit` — the typed form (an add with no
 * certificate, or correcting a record).
 */
type Phase = 'upload' | 'review' | 'view' | 'edit' | 'confirm-delete';

const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp';
const ACCEPTED_TYPES: ReadonlySet<string> = new Set(ACCEPT.split(','));
const MAX_CERTIFICATE_BYTES = 10 * 1024 * 1024;

type CertificateVaccine = Extract<VaccinationCertificateRead, { kind: 'found' }>['vaccines'][number];

/** The uploaded certificate, while this dialog is open. */
interface Certificate {
  file: File;
  /** A local preview URL, or null where the browser cannot make one. */
  url: string | null;
  read: { kind: 'reading' } | VaccinationCertificateRead;
  /** The vaccine whose values are in the form, when one was picked. */
  picked: CertificateVaccine | null;
  /** Vaccines already added from this certificate in this sitting. */
  added: ReadonlyArray<string>;
}

/** The vaccines on the certificate not yet added from it. */
function remainingOn(certificate: Certificate | null): CertificateVaccine[] {
  if (!certificate || certificate.read.kind !== 'found') return [];
  return certificate.read.vaccines.filter((v) => !certificate.added.includes(v.name));
}

/** "Rabies", "Rabies and DHPP", "Rabies, DHPP and Bordetella". */
function listOf(names: ReadonlyArray<string>): string {
  const labels = names.map(vaccineLabel);
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/** `YYYY-MM-DD` for a date input from an ISO date/datetime (UTC day). */
function isoDay(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}


export function VaccinationRecordDialog({
  apiUrl,
  dogId,
  dogName,
  mode,
  knownNames,
  onChanged,
  onViewCertificate,
  onViewOriginal,
  onAddAnother,
  onClose,
}: VaccinationRecordDialogProps): JSX.Element {
  const record = mode.kind === 'manage' ? mode.record : null;
  const [phase, setPhase] = useState<Phase>(mode.kind === 'add' ? 'upload' : 'view');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // The edit form. The "checked" box starts at the record's sign-off, and a
  // change to the vaccine or a date clears it once: the old check covered the
  // old values (the server applies the same rule).
  const [name, setName] = useState(record ? record.name : '');
  const [dateGiven, setDateGiven] = useState(isoDay(record?.dateGiven ?? null));
  const [expiresAt, setExpiresAt] = useState(isoDay(record?.expiresAt ?? null));
  const [checked, setChecked] = useState(record?.verified === true);
  const [checkedTouched, setCheckedTouched] = useState(false);

  const [certificate, setCertificate] = useState<Certificate | null>(null);
  /** The form was typed in since the certificate was chosen: don't overwrite it. */
  const typedRef = useRef(false);
  /** Bumped per chosen file, so a slow read of an earlier file is ignored. */
  const readSeq = useRef(0);

  const panelRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const certificateFileRef = useRef<HTMLInputElement>(null);

  // The local preview lives exactly as long as its certificate.
  const previewUrl = certificate?.url ?? null;
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Focus in on open, back to whatever opened it on close.
  useEffect(() => {
    const opener = globalThis.document?.activeElement as HTMLElement | null;
    panelRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    return () => opener?.focus?.();
  }, []);

  const resetForm = (): void => {
    setName(record ? record.name : '');
    setDateGiven(isoDay(record?.dateGiven ?? null));
    setExpiresAt(isoDay(record?.expiresAt ?? null));
    setChecked(record?.verified === true);
    setCheckedTouched(false);
  };

  const valueChanged = (): void => {
    typedRef.current = true;
    if (!checkedTouched) setChecked(false);
  };

  /** Put one vaccine from the certificate into the form. */
  const fill = (v: CertificateVaccine | null): void => {
    setName(v ? vaccineLabel(v.name) : '');
    setDateGiven(v?.dateGiven ?? '');
    setExpiresAt(v?.expiresAt ?? '');
    setCertificate((c) => (c ? { ...c, picked: v } : c));
  };

  /** The certificate is chosen: show it at once, then read it. */
  const onCertificateFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setDone(null);
    if (file.size > MAX_CERTIFICATE_BYTES) {
      setError('That file is over 10MB. Try a photo, or a smaller PDF.');
      return;
    }
    if (file.type && !ACCEPTED_TYPES.has(file.type)) {
      setError('Choose a PDF, or a JPEG, PNG or WebP photo of the certificate.');
      return;
    }
    const { token } = readSession();
    if (!token) {
      setError('Your session has expired. Sign in again to save.');
      return;
    }
    setError(null);
    const url = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null;
    const seq = ++readSeq.current;
    typedRef.current = false;
    setName('');
    setDateGiven('');
    setExpiresAt('');
    setCertificate({ file, url, read: { kind: 'reading' }, picked: null, added: [] });
    setPhase('review');
    void readDogVaccinationCertificate(apiUrl, token, dogId, file).then((read) => {
      if (seq !== readSeq.current) return;
      const first = read.kind === 'found' ? read.vaccines[0] ?? null : null;
      setCertificate((c) => (c ? { ...c, read } : c));
      // Fill only a form nobody has started typing into.
      if (first && !typedRef.current) fill(first);
    });
  };

  /**
   * Save from the certificate: create the record unverified, attach the
   * same file through the attach route, and only on the Verify tap mark it
   * verified — after the certificate is on file. Each step that fails says
   * what DID happen, so nothing is left half-known.
   */
  const onSaveFromCertificate = async (verify: boolean): Promise<void> => {
    if (!certificate) return;
    if (name.trim().length === 0 || !dateGiven || !expiresAt) {
      setError('Enter the vaccine, the date it was given and the date it expires.');
      return;
    }
    const { token } = readSession();
    if (!token) {
      setError('Your session has expired. Sign in again to save.');
      return;
    }
    const label = vaccineLabel(name.trim());
    const message = (err: unknown): string =>
      humanizeApiError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
    setBusy(true);
    setError(null);
    setDone(null);
    let id: string;
    try {
      ({ id } = await addDogVaccination(apiUrl, token, dogId, {
        name: name.trim(),
        dateGiven,
        expiresAt,
        verified: false,
      }));
    } catch (err) {
      setError(message(err));
      setBusy(false);
      return;
    }
    let problem: string | null = null;
    try {
      await uploadDogVaccinationDocument(apiUrl, token, dogId, id, certificate.file);
    } catch (err) {
      problem = verify
        ? `${label} was added but not verified, because the certificate didn’t attach: ${message(err)} Attach it below, then mark it verified.`
        : `${label} was added, but the certificate didn’t attach: ${message(err)} Attach it below.`;
    }
    if (verify && problem === null) {
      try {
        await verifyDogVaccination(apiUrl, token, dogId, id);
      } catch (err) {
        problem = `${label} was added with the certificate attached, but it isn’t verified yet: ${message(err)} Tap Mark verified to try again.`;
      }
    }
    const addedName = certificate.picked?.name ?? name.trim().toLowerCase();
    setCertificate((c) => (c ? { ...c, added: [...c.added, addedName] } : c));
    try {
      await onChanged(id);
    } catch {
      // The profile re-read failing does not undo what saved; the page's own
      // load state says so.
    }
    if (problem) setError(problem);
    else
      setDone(
        verify
          ? `${label} added and verified, with the certificate attached.`
          : `${label} added with the certificate attached. It isn’t verified yet.`,
      );
    setPhase('view');
    setBusy(false);
  };

  /** Back to add mode on the same certificate, for its next vaccine. */
  const onAddNext = (next: CertificateVaccine): void => {
    setError(null);
    setDone(null);
    setChecked(false);
    setCheckedTouched(false);
    typedRef.current = false;
    fill(next);
    setPhase('review');
    onAddAnother?.();
  };

  /**
   * One write: the session token, busy state, the server's sentence on
   * failure, and a re-read of the profile on success. The action may return
   * the id of a record to keep open (an add opens what it created).
   */
  async function run(
    action: (token: string) => Promise<string | void>,
    success: string,
  ): Promise<boolean> {
    const { token } = readSession();
    if (!token) {
      setError('Your session has expired. Sign in again to save.');
      return false;
    }
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const focusId = await action(token);
      await onChanged(focusId ?? undefined);
      setDone(success);
      return true;
    } catch (err) {
      setError(humanizeApiError(err instanceof Error ? err.message : 'Something went wrong. Try again.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const onSave = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (name.trim().length === 0 || !dateGiven || !expiresAt) {
      setError('Enter the vaccine, the date it was given and the date it expires.');
      return;
    }
    const body = { name: name.trim(), dateGiven, expiresAt, verified: checked };
    if (mode.kind === 'add') {
      const ok = await run(
        async (t) => (await addDogVaccination(apiUrl, t, dogId, body)).id,
        `${vaccineLabel(body.name)} added. Attach the certificate if you have it.`,
      );
      if (ok) setPhase('view');
      return;
    }
    const ok = await run((t) => updateDogVaccination(apiUrl, t, dogId, record!.id, body), 'Saved.');
    if (ok) setPhase('view');
  };

  const onVerify = (): void => {
    void run((t) => verifyDogVaccination(apiUrl, t, dogId, record!.id), 'Marked verified.');
  };

  const onDelete = async (): Promise<void> => {
    const ok = await run((t) => deleteDogVaccination(apiUrl, t, dogId, record!.id), 'Deleted.');
    if (ok) onClose();
  };

  const onFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !record) return;
    const replacing = !!record.document;
    void run(
      (t) => uploadDogVaccinationDocument(apiUrl, t, dogId, record.id, file),
      replacing ? 'Certificate replaced.' : 'Certificate attached.',
    );
  };

  const title = record ? vaccineLabel(record.name) : 'Add a vaccination';
  const standing = record
    ? vaccinationStanding({ verified: record.verified, source: record.source ?? null })
    : null;
  const source = record ? parseVaccinationSource(record.source ?? null) : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="vax-record-title"
      className="ferro-blocking-dialog__overlay"
      data-testid="vax-record-dialog"
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || busy) return;
        event.stopPropagation();
        onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`ferro-blocking-dialog__panel ferro-vax-record${
          phase === 'review' ? ' ferro-vax-record--compare' : ''
        }`}
      >
        <div className="ferro-vax-record__head">
          <div>
            <p className="ferro-vax-record__eyebrow">{dogName} · vaccination record</p>
            <h2 id="vax-record-title">{title}</h2>
          </div>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-vax-record__close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            data-testid="vax-record-close"
            {...(phase === 'view' ? { 'data-autofocus': true } : {})}
          >
            Close
          </button>
        </div>

        {done ? (
          <p className="ferro-vax-record__done" role="status" data-testid="vax-record-done">
            {done}
          </p>
        ) : null}

        {record && phase === 'view' ? (
          <>
            <dl className="ferro-vax-record__facts">
              <div>
                <dt>Given</dt>
                <dd data-testid="vax-record-given">{formatDate(record.dateGiven) ?? 'Not on file'}</dd>
              </div>
              <div>
                <dt>Expires</dt>
                <dd data-testid="vax-record-expires">{formatDate(record.expiresAt) ?? 'Not on file'}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{VAX_ROW_LABEL[vaxRowDisplay(record)]}</dd>
              </div>
              <div>
                <dt>Verification</dt>
                <dd data-testid="vax-record-standing">
                  {standing ? VACCINATION_STANDING_LABEL[standing.kind] : '—'}
                  {standing?.kind === 'verified' && record.verifiedByName ? ` · ${record.verifiedByName}` : ''}
                </dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd>{source ? VACCINATION_SOURCE_LABEL[source] : 'Not recorded'}</dd>
              </div>
              <div>
                <dt>Certificate</dt>
                <dd data-testid="vax-record-certificate">
                  {record.document
                    ? `${record.document.filename ?? 'Attached'} · ${formatDate(record.document.uploadedAt) ?? ''}`
                    : 'None attached'}
                </dd>
              </div>
            </dl>

            {error ? (
              <p className="ferro-dogprofile__form-error" role="alert" data-testid="vax-record-error">
                {error}
              </p>
            ) : null}

            <div className="ferro-vax-record__actions">
              {!record.verified ? (
                <button
                  type="button"
                  className="ferro-btn ferro-btn--primary"
                  onClick={onVerify}
                  disabled={busy}
                  data-testid="vax-record-verify"
                >
                  {busy ? 'Saving…' : 'Mark verified'}
                </button>
              ) : null}
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => {
                  resetForm();
                  setError(null);
                  setDone(null);
                  setPhase('edit');
                }}
                disabled={busy}
                data-testid="vax-record-edit"
              >
                Correct details
              </button>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                data-testid="vax-record-attach"
              >
                {record.document ? 'Replace certificate' : 'Attach certificate'}
              </button>
              {record.document ? (
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={() => onViewCertificate(record)}
                  disabled={busy}
                  data-testid="vax-record-view-certificate"
                >
                  View certificate
                </button>
              ) : onViewOriginal ? (
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={onViewOriginal}
                  disabled={busy}
                  data-testid="vax-record-view-original"
                >
                  View uploaded record
                </button>
              ) : null}
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost ferro-vax-record__delete"
                onClick={() => {
                  setError(null);
                  setDone(null);
                  setPhase('confirm-delete');
                }}
                disabled={busy}
                data-testid="vax-record-delete"
              >
                Delete record
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              className="ferro-vax-record__file"
              onChange={onFile}
              data-testid="vax-record-file"
              aria-label={`Certificate file for ${title}`}
              tabIndex={-1}
            />
            <p className="ferro-vax-record__hint">
              A PDF or a photo of the certificate, up to 10MB. Every change is recorded with your name and the time.
            </p>
            {onAddAnother && remainingOn(certificate).length > 0 ? (
              <div className="ferro-vax-record__next" data-testid="vax-record-next">
                <p className="ferro-vax-record__hint">
                  Also on this certificate: {listOf(remainingOn(certificate).map((v) => v.name))}.
                </p>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={() => onAddNext(remainingOn(certificate)[0]!)}
                  disabled={busy}
                  data-testid="vax-record-add-next"
                >
                  Add {vaccineLabel(remainingOn(certificate)[0]!.name)} from it
                </button>
              </div>
            ) : null}
          </>
        ) : null}

        <input
          ref={certificateFileRef}
          type="file"
          accept={ACCEPT}
          className="ferro-vax-record__file"
          onChange={onCertificateFile}
          data-testid="vax-record-certificate-file"
          aria-label={`Certificate file for ${dogName}`}
          tabIndex={-1}
        />

        {phase === 'upload' ? (
          <div className="ferro-vax-record__upload" data-testid="vax-record-upload-step">
            <p className="ferro-vax-record__lead">
              Start with the certificate. The vaccine and dates fill in from it, and you check them against it before
              you verify.
            </p>
            {error ? (
              <p className="ferro-dogprofile__form-error" role="alert" data-testid="vax-record-error">
                {error}
              </p>
            ) : null}
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              onClick={() => certificateFileRef.current?.click()}
              data-autofocus
              data-testid="vax-record-upload"
            >
              Upload the certificate
            </button>
            <p className="ferro-vax-record__hint">A PDF or a photo of the certificate, up to 10MB.</p>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost ferro-vax-record__alt"
              onClick={() => {
                setError(null);
                setPhase('edit');
              }}
              data-testid="vax-record-type-instead"
            >
              No certificate? Type it in
            </button>
          </div>
        ) : null}

        {phase === 'review' && certificate ? (
          <div className="ferro-vax-record__compare" data-testid="vax-record-compare">
            <CertificatePreview
              certificate={certificate}
              busy={busy}
              onChooseAnother={() => certificateFileRef.current?.click()}
            />
            <form
              className="ferro-vax-record__form"
              onSubmit={(e) => {
                e.preventDefault();
                void onSaveFromCertificate(true);
              }}
              noValidate
              data-testid="vax-record-review-form"
            >
              <ReadStatus certificate={certificate} />
              {certificate.read.kind === 'found' && certificate.read.vaccines.length > 1 ? (
                <div className="ferro-vax-record__picker">
                  <p className="ferro-dogprofile__field-label" id="vax-record-picker-label">
                    On this certificate
                  </p>
                  <div className="ferro-segmented" role="group" aria-labelledby="vax-record-picker-label">
                    {certificate.read.vaccines.map((v) => {
                      const added = certificate.added.includes(v.name);
                      const active = certificate.picked?.name === v.name;
                      return (
                        <button
                          key={v.name}
                          type="button"
                          className={`ferro-segmented__btn${active ? ' ferro-segmented__btn--active' : ''}`}
                          aria-pressed={active}
                          disabled={busy || added}
                          onClick={() => {
                            typedRef.current = false;
                            fill(v);
                          }}
                          data-testid={`vax-record-pick-${v.name}`}
                        >
                          {vaccineLabel(v.name)}
                          {added ? ' · added' : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              <FieldLabel
                htmlFor="vax-record-name"
                text="Vaccine"
                fromCertificate={!!certificate.picked && name === vaccineLabel(certificate.picked.name)}
              />
              <input
                id="vax-record-name"
                className="ferro-dogprofile__input"
                type="text"
                list="vax-record-names"
                autoComplete="off"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  valueChanged();
                }}
                maxLength={100}
                disabled={busy}
                data-testid="vax-record-name"
              />
              <datalist id="vax-record-names">
                {knownNames.map((n) => (
                  <option key={n} value={vaccineLabel(n)}>
                    {vaccineLabel(n)}
                  </option>
                ))}
              </datalist>
              <div className="ferro-vax-record__dates">
                <div className="ferro-vax-record__date">
                  <FieldLabel
                    htmlFor="vax-record-given"
                    text="Date given"
                    fromCertificate={!!certificate.picked?.dateGiven && dateGiven === certificate.picked.dateGiven}
                  />
                  <input
                    id="vax-record-given"
                    className="ferro-dogprofile__input"
                    type="date"
                    value={dateGiven}
                    onChange={(e) => {
                      setDateGiven(e.target.value);
                      valueChanged();
                    }}
                    disabled={busy}
                    data-testid="vax-record-given-input"
                  />
                  {certificate.picked && !certificate.picked.dateGiven && !dateGiven ? (
                    <p className="ferro-vax-record__hint" data-testid="vax-record-given-missing">
                      Not readable on the certificate. Type it from the document.
                    </p>
                  ) : null}
                </div>
                <div className="ferro-vax-record__date">
                  <FieldLabel
                    htmlFor="vax-record-expires"
                    text="Expires"
                    fromCertificate={!!certificate.picked?.expiresAt && expiresAt === certificate.picked.expiresAt}
                  />
                  <input
                    id="vax-record-expires"
                    className="ferro-dogprofile__input"
                    type="date"
                    value={expiresAt}
                    onChange={(e) => {
                      setExpiresAt(e.target.value);
                      valueChanged();
                    }}
                    disabled={busy}
                    data-testid="vax-record-expires-input"
                  />
                  {certificate.picked && !certificate.picked.expiresAt && !expiresAt ? (
                    <p className="ferro-vax-record__hint" data-testid="vax-record-expires-missing">
                      The certificate doesn’t print an expiry. Type it from the document.
                    </p>
                  ) : null}
                </div>
              </div>
              {error ? (
                <p className="ferro-dogprofile__form-error" role="alert" data-testid="vax-record-error">
                  {error}
                </p>
              ) : null}
              <p className="ferro-vax-record__hint">
                Verify when every field matches the certificate. Not sure yet? Save it, and it stays unverified.
              </p>
              <div className="ferro-blocking-dialog__actions">
                <button type="button" className="ferro-btn ferro-btn--ghost" onClick={onClose} disabled={busy}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={() => void onSaveFromCertificate(false)}
                  disabled={busy || certificate.read.kind === 'reading'}
                  data-testid="vax-record-save-unverified"
                >
                  Save, not verified
                </button>
                <button
                  type="submit"
                  className="ferro-btn ferro-btn--primary"
                  disabled={busy || certificate.read.kind === 'reading'}
                  data-testid="vax-record-verify-save"
                >
                  {busy ? 'Saving…' : 'Verify'}
                </button>
              </div>
            </form>
          </div>
        ) : null}

        {phase === 'edit' ? (
          <form className="ferro-vax-record__form" onSubmit={onSave} noValidate data-testid="vax-record-form">
            <label className="ferro-dogprofile__field-label" htmlFor="vax-record-name">
              Vaccine
            </label>
            <input
              id="vax-record-name"
              className="ferro-dogprofile__input"
              type="text"
              list="vax-record-names"
              autoComplete="off"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                valueChanged();
              }}
              maxLength={100}
              disabled={busy}
              data-autofocus
              data-testid="vax-record-name"
            />
            <datalist id="vax-record-names">
              {knownNames.map((n) => (
                <option key={n} value={n}>
                  {vaccineLabel(n)}
                </option>
              ))}
            </datalist>
            <div className="ferro-vax-record__dates">
              <div className="ferro-vax-record__date">
                <label className="ferro-dogprofile__field-label" htmlFor="vax-record-given">
                  Date given
                </label>
                <input
                  id="vax-record-given"
                  className="ferro-dogprofile__input"
                  type="date"
                  value={dateGiven}
                  onChange={(e) => {
                    setDateGiven(e.target.value);
                    valueChanged();
                  }}
                  disabled={busy}
                  data-testid="vax-record-given-input"
                />
              </div>
              <div className="ferro-vax-record__date">
                <label className="ferro-dogprofile__field-label" htmlFor="vax-record-expires">
                  Expires
                </label>
                <input
                  id="vax-record-expires"
                  className="ferro-dogprofile__input"
                  type="date"
                  value={expiresAt}
                  onChange={(e) => {
                    setExpiresAt(e.target.value);
                    valueChanged();
                  }}
                  disabled={busy}
                  data-testid="vax-record-expires-input"
                />
              </div>
            </div>
            <label className="ferro-vax-record__check">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => {
                  setChecked(e.target.checked);
                  setCheckedTouched(true);
                }}
                disabled={busy}
                data-testid="vax-record-checked"
              />
              <span>I checked these dates against the certificate</span>
            </label>
            <p className="ferro-vax-record__hint">
              {mode.kind === 'add'
                ? 'Leave it unticked if you have not seen the certificate yet.'
                : 'Changing the vaccine or a date makes this the desk’s own entry, and clears an earlier check until you tick it again.'}
            </p>
            {error ? (
              <p className="ferro-dogprofile__form-error" role="alert" data-testid="vax-record-error">
                {error}
              </p>
            ) : null}
            <div className="ferro-blocking-dialog__actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => {
                  setError(null);
                  if (mode.kind === 'add') onClose();
                  else setPhase('view');
                }}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="ferro-btn ferro-btn--primary"
                disabled={busy}
                data-testid="vax-record-save"
              >
                {busy ? 'Saving…' : mode.kind === 'add' ? 'Add vaccination' : 'Save'}
              </button>
            </div>
          </form>
        ) : null}

        {record && phase === 'confirm-delete' ? (
          <div className="ferro-vax-record__confirm" data-testid="vax-record-confirm-delete">
            <p className="ferro-vax-record__confirm-lead">
              <strong>Delete this {title} record?</strong> Given {formatDate(record.dateGiven) ?? 'on an unknown date'},
              expires {formatDate(record.expiresAt) ?? 'never'}
              {source ? `, from ${VACCINATION_SOURCE_LABEL[source]}` : ''}.
            </p>
            <p className="ferro-vax-record__hint">
              It leaves {dogName}&rsquo;s profile and can&rsquo;t be restored here.
              {record.document ? ' Its attached certificate is deleted with it.' : ''} The deletion is recorded with
              your name.
            </p>
            {error ? (
              <p className="ferro-dogprofile__form-error" role="alert" data-testid="vax-record-error">
                {error}
              </p>
            ) : null}
            <div className="ferro-blocking-dialog__actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => setPhase('view')}
                disabled={busy}
                data-autofocus
              >
                Keep it
              </button>
              <button
                type="button"
                className="ferro-btn ferro-btn--danger"
                onClick={() => void onDelete()}
                disabled={busy}
                data-testid="vax-record-confirm-delete-yes"
              >
                {busy ? 'Deleting…' : 'Delete record'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A field label, marked while its value is still the certificate's. */
function FieldLabel({
  htmlFor,
  text,
  fromCertificate,
}: {
  htmlFor: string;
  text: string;
  fromCertificate: boolean;
}): JSX.Element {
  return (
    <label className="ferro-dogprofile__field-label ferro-vax-record__label" htmlFor={htmlFor}>
      {text}
      {fromCertificate ? (
        <span className="ferro-vax-record__from" data-testid={`${htmlFor}-from-certificate`}>
          From certificate
        </span>
      ) : null}
    </label>
  );
}

/** What the read found, in one sentence the desk can act on. */
function ReadStatus({ certificate }: { certificate: Certificate }): JSX.Element {
  const read = certificate.read;
  switch (read.kind) {
    case 'reading':
      return (
        <p className="ferro-vax-record__read" role="status" data-testid="vax-record-read-status" data-read="reading">
          Reading the certificate…
        </p>
      );
    case 'found':
      return (
        <div
          className={`ferro-vax-record__read${read.lowConfidence ? ' ferro-vax-record__read--unsure' : ''}`}
          role="status"
          data-testid="vax-record-read-status"
          data-read="found"
        >
          <p>Filled in from the certificate. Check each field against it, then tap Verify.</p>
          {read.lowConfidence ? (
            <p>The certificate was hard to read, so check every field closely.</p>
          ) : null}
        </div>
      );
    case 'nothing_found':
      return (
        <div
          className="ferro-vax-record__read ferro-vax-record__read--unsure"
          role="status"
          data-testid="vax-record-read-status"
          data-read="nothing_found"
        >
          <p>
            <strong>We couldn’t read a vaccine from this document.</strong>
          </p>
          <p>If it is the vaccination certificate, type the details from it. If not, choose a different file.</p>
        </div>
      );
    case 'unreadable':
      return (
        <p
          className="ferro-vax-record__read ferro-vax-record__read--unsure"
          role="status"
          data-testid="vax-record-read-status"
          data-read="unreadable"
        >
          {read.message}
        </p>
      );
    default: {
      const unreachable: never = read;
      return unreachable;
    }
  }
}

/** The uploaded document itself, beside the form. */
function CertificatePreview({
  certificate,
  busy,
  onChooseAnother,
}: {
  certificate: Certificate;
  busy: boolean;
  onChooseAnother: () => void;
}): JSX.Element {
  const { file, url } = certificate;
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  return (
    <figure className="ferro-vax-record__doc" data-testid="vax-record-doc">
      <div className="ferro-vax-record__doc-stage">
        {url === null ? (
          <p className="ferro-vax-record__hint">This browser can’t show a preview. Open the file to compare.</p>
        ) : isPdf ? (
          <iframe src={url} title={`Certificate: ${file.name}`} data-testid="vax-record-doc-pdf" />
        ) : (
          <img src={url} alt={`Certificate: ${file.name}`} data-testid="vax-record-doc-image" />
        )}
      </div>
      <figcaption className="ferro-vax-record__doc-bar">
        <span className="ferro-vax-record__doc-name">{file.name}</span>
        <span className="ferro-vax-record__doc-actions">
          {url ? (
            <a className="ferro-btn ferro-btn--ghost" href={url} target="_blank" rel="noopener noreferrer">
              Open full size
            </a>
          ) : null}
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={onChooseAnother}
            disabled={busy}
            data-testid="vax-record-choose-another"
          >
            Choose a different file
          </button>
        </span>
      </figcaption>
    </figure>
  );
}
